<script>
/**
 * 매수·매도(호가) 상세 페이지.
 *
 * 구성 섹션:
 * 1. 회원 정보 (간단): 회원코드, 이름, 연락처, 추천지점, 이용지점
 * 2. 매수·매도 정보: 호가코드, 구분, 현물 구분, 가격, 중량, 거래금액, 상태, 등록일
 * 3. 수수료 정산: 총수수료, 본사수수료, 추천지점수수료, 이용지점수수료
 *    - trade_side에 따라 판매수수료/구매수수료 구분 표시
 *
 * 권한:
 * - HQ 관리자: 전체 조회
 * - 지점 관리자: 자기 지점 관련 호가만 조회 가능
 */
import { getTradeDetail } from '@/api/trade.js'

export default {
  name: 'TradeDetailPage',

  data() {
    return {
      /** 호가코드 (URL 파라미터) */
      p2pAskCode: this.$route.params.p2pAskCode,

      /* ── 상세 데이터 ── */
      detail: null,

      /* ── UI 상태 ── */
      loading: false,
    }
  },

  mounted() {
    this.fetchDetail()
  },

  watch: {
    '$route.params.p2pAskCode'(newCode) {
      this.p2pAskCode = newCode
      this.fetchDetail()
    },
  },

  methods: {
    /**
     * 서버에서 매수·매도 상세 정보를 조회한다.
     */
    async fetchDetail() {
      if (!this.p2pAskCode) {
        this.$router.push('/trade')
        return
      }
      this.loading = true
      try {
        const res = await getTradeDetail(this.p2pAskCode)
        if (res.data?.code === 0) {
          this.detail = res.data.data
        } else {
          this.$router.push('/trade')
        }
      } catch (e) {
        console.error('[TradeDetailPage] fetchDetail error:', e)
        this.$router.push('/trade')
      } finally {
        this.loading = false
      }
    },

    /** 목록으로 돌아가기 */
    goBack() {
      this.$router.push('/trade')
    },

    /** 금액을 천단위 콤마 포맷으로 변환 */
    formatAmount(val) {
      if (val == null || val === '') return '-'
      return Number(val).toLocaleString()
    },

    /** 날짜/시간 포맷 */
    formatDateTime(val) {
      if (!val) return '-'
      return String(val).substring(0, 16).replace('T', ' ')
    },
  },
}
</script>

<template>
  <div>
    <!-- 페이지 헤더 -->
    <div class="d-flex align-center justify-space-between mb-5">
      <div>
        <div class="d-flex align-center ga-1 text-caption text-medium-emphasis mb-1">
          <span class="breadcrumb-link text-primary" @click="goBack">매수·매도</span>
          <v-icon size="12">mdi-chevron-right</v-icon>
          <span>상세</span>
        </div>
        <div class="text-h5 font-weight-bold">매수·매도 상세</div>
        <div class="text-body-2 text-grey mt-1">호가 정보 및 수수료 정산 내역</div>
      </div>
    </div>

    <!-- 로딩 스켈레톤 -->
    <v-row v-if="loading">
      <v-col v-for="i in 3" :key="i" cols="12" md="6">
        <v-skeleton-loader type="card" />
      </v-col>
    </v-row>

    <template v-else-if="detail">
      <v-row>
        <!-- 1. 회원 정보 카드 (상단) -->
        <v-col cols="12">
          <v-card rounded="xl" elevation="0" class="detail-card mb-4">
            <div class="detail-card-header d-flex align-center ga-2 px-5 py-4">
              <div class="header-icon-wrap bg-primary-soft">
                <v-icon color="primary" size="18">mdi-account-outline</v-icon>
              </div>
              <span class="text-subtitle-2 font-weight-bold">회원 정보</span>
            </div>
            <v-divider />
            <v-card-text class="pa-5">
              <!-- 1행: 회원코드 - 이름 - 연락처 -->
              <div class="dl-grid">
                <div class="dl-item">
                  <div class="dl-label">이름</div>
                  <div class="dl-value font-weight-bold">{{ detail.userName || '-' }}</div>
                </div>
                <div class="dl-item">
                  <div class="dl-label">연락처</div>
                  <div class="dl-value">{{ detail.userPhone || '-' }}</div>
                </div>
              <!-- 2행: 추천지점 - 이용지점 -->
                <div class="dl-item">
                  <div class="dl-label">추천지점</div>
                  <div class="dl-value">
                    <template v-if="detail.recBranchName">
                      <span class="font-weight-medium">{{ detail.recBranchName }}</span>
                      <span v-if="detail.recBranchCode" class="text-caption text-medium-emphasis ml-1">({{ detail.recBranchCode }})</span>
                    </template>
                    <span v-else class="text-medium-emphasis">-</span>
                  </div>
                </div>
                <div class="dl-item">
                  <div class="dl-label">이용지점</div>
                  <div class="dl-value">
                    <template v-if="detail.useBranchName">
                      <span class="font-weight-medium">{{ detail.useBranchName }}</span>
                      <span v-if="detail.useBranchCode" class="text-caption text-medium-emphasis ml-1">({{ detail.useBranchCode }})</span>
                    </template>
                    <span v-else class="text-medium-emphasis">-</span>
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- 2. 매수·매도 정보 카드 (회원 정보 아래) -->
        <v-col cols="12">
          <v-card rounded="xl" elevation="0" class="detail-card mb-4">
            <div class="detail-card-header d-flex align-center ga-2 px-5 py-4">
              <div class="header-icon-wrap bg-amber-soft">
                <v-icon color="amber-darken-2" size="18">mdi-swap-horizontal</v-icon>
              </div>
              <span class="text-subtitle-2 font-weight-bold">매수·매도 정보</span>
            </div>
            <v-divider />
            <v-card-text class="pa-5">
              <div class="dl-grid">
                <div class="dl-item">
                  <div class="dl-label">호가코드</div>
                  <div class="dl-value text-caption text-medium-emphasis font-weight-medium">{{ detail.p2pAskCode || '-' }}</div>
                </div>
                <div class="dl-item">
                  <div class="dl-label">구분</div>
                  <div class="dl-value">
                    <v-chip
                      :color="detail.tradeSide === 'BUY' ? 'primary' : 'error'"
                      size="x-small"
                      label
                      variant="tonal"
                      class="font-weight-medium"
                    >
                      {{ detail.tradeSideLabel || '-' }}
                    </v-chip>
                  </div>
                </div>
                <div class="dl-item">
                  <div class="dl-label">현물 구분</div>
                  <div class="dl-value">
                    <v-chip size="x-small" color="primary" variant="tonal" label class="font-weight-medium">
                      {{ detail.spotTypeLabel || '-' }}
                    </v-chip>
                  </div>
                </div>
                <div class="dl-item">
                  <div class="dl-label">가격(1g)</div>
                  <div class="dl-value font-weight-medium">{{ formatAmount(detail.price) }}원</div>
                </div>
                <div class="dl-item">
                  <div class="dl-label">중량</div>
                  <div class="dl-value">{{ detail.weight != null ? detail.weight : '-' }}g</div>
                </div>
                <div class="dl-item">
                  <div class="dl-label">거래금액</div>
                  <div class="dl-value font-weight-bold">{{ formatAmount(detail.tradeAmount) }}원</div>
                </div>
                <div class="dl-item">
                  <div class="dl-label">상태</div>
                  <div class="dl-value">
                    <v-chip
                      size="x-small"
                      :color="detail.status === 'FILLED' ? 'success' : detail.status === 'OPEN' ? 'info' : 'grey'"
                      variant="tonal"
                      label
                      class="font-weight-medium"
                    >
                      {{ detail.status || '-' }}
                    </v-chip>
                  </div>
                </div>
                <div class="dl-item">
                  <div class="dl-label">등록일</div>
                  <div class="dl-value text-medium-emphasis">{{ formatDateTime(detail.insertDt) }}</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- 3. 수수료 정산 카드 -->
        <v-col cols="12">
          <v-card rounded="xl" elevation="0" class="detail-card">
            <div class="detail-card-header d-flex align-center ga-2 px-5 py-4">
              <div class="header-icon-wrap bg-teal-soft">
                <v-icon color="teal" size="18">mdi-cash-multiple</v-icon>
              </div>
              <span class="text-subtitle-2 font-weight-bold">수수료 정산</span>
              <v-spacer />
              <span class="text-caption text-medium-emphasis">판매 시 판매수수료, 구매 시 구매수수료 적용</span>
            </div>
            <v-divider />
            <v-card-text class="pa-5">
              <template v-if="detail.feeSettlements && detail.feeSettlements.length > 0">
                <v-table density="compact" class="border-t">
                  <thead>
                    <tr>
                      <th>구분</th>
                      <th>가격(1g)</th>
                      <th>중량</th>
                      <th>거래금액</th>
                      <th>총수수료</th>
                      <th>본사수수료</th>
                      <th>추천지점 수수료</th>
                      <th>이용지점 수수료</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(fs, idx) in detail.feeSettlements" :key="'fs-' + idx">
                      <td>
                        <v-chip size="x-small" label variant="tonal" class="font-weight-medium">
                          {{ fs.tradeSideLabel }}
                        </v-chip>
                      </td>
                      <td>{{ formatAmount(fs.price) }}원</td>
                      <td>{{ fs.weight }}g</td>
                      <td>{{ formatAmount(fs.tradeAmount) }}원</td>
                      <td class="font-weight-medium">{{ formatAmount(fs.totalFeeAmount) }}원</td>
                      <td>{{ formatAmount(fs.hqFeeAmount) }}원</td>
                      <td>
                        <span v-if="fs.recBranchName">{{ fs.recBranchName }}: {{ formatAmount(fs.recBranchFeeAmount) }}원</span>
                        <span v-else class="text-medium-emphasis">-</span>
                      </td>
                      <td>
                        <span v-if="fs.useBranchName">{{ fs.useBranchName }}: {{ formatAmount(fs.useBranchFeeAmount) }}원</span>
                        <span v-else class="text-medium-emphasis">-</span>
                      </td>
                    </tr>
                  </tbody>
                </v-table>
              </template>
              <div v-else class="empty-state">
                <v-icon size="36" color="grey-lighten-2">mdi-cash-remove-outline</v-icon>
                <div class="text-caption text-medium-emphasis mt-2">수수료 정산 내역이 없습니다.</div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </template>
  </div>
</template>

<style scoped>
/* ── 공통 카드 ── */
.detail-card {
  border: 1px solid rgba(0, 0, 0, 0.08) !important;
}

.detail-card-header {
  background: rgba(0, 0, 0, 0.018);
}

/* ── 헤더 아이콘 ── */
.header-icon-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  flex-shrink: 0;
}
.bg-primary-soft { background: rgba(var(--v-theme-primary), 0.1); }
.bg-amber-soft  { background: rgba(255, 193, 7, 0.12); }
.bg-teal-soft    { background: rgba(0, 150, 136, 0.1); }

/* ── 브레드크럼 ── */
.breadcrumb-link {
  cursor: pointer;
}
.breadcrumb-link:hover {
  text-decoration: underline;
}

/* ── 정의 리스트 ── */
.dl-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px 16px;
}

.dl-item {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.dl-label {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  color: rgba(0, 0, 0, 0.38);
  text-transform: uppercase;
}

.dl-value {
  font-size: 0.875rem;
  color: rgba(0, 0, 0, 0.82);
  line-height: 1.4;
}

.member-info-row .dl-item {
  min-width: 120px;
}

/* ── 빈 상태 ── */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 0;
}
</style>
